import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { ProductListReviewsCollection, ReviewsInfo } from '@reviews/const';
import { StateUtils } from '@spartacus/core';
import { REVIEWS_FEATURE, ReviewsFeatureState, StateWithReviews } from '../reviews-state';

export const getCustomReviewsState: MemoizedSelector<StateWithReviews, ReviewsFeatureState> =
  createFeatureSelector<ReviewsFeatureState>(REVIEWS_FEATURE);

type ReviewsState = ReviewsFeatureState['reviews'];
type AddReviewState = ReviewsFeatureState['addReview'];
type ProductListReviewState = ReviewsFeatureState['productListReview'];

export const getReviewsState: MemoizedSelector<StateWithReviews, ReviewsState> = createSelector(
  getCustomReviewsState,
  (state: ReviewsFeatureState) => state.reviews,
);

export const getReviews = (
  productCode: string,
): MemoizedSelector<StateWithReviews, StateUtils.LoaderState<ReviewsInfo>> =>
  createSelector(getReviewsState, (state: ReviewsState) =>
    StateUtils.entityLoaderStateSelector<ReviewsInfo>(state, productCode),
  );

export const getReviewsValue = (productCode: string): MemoizedSelector<StateWithReviews, ReviewsInfo> =>
  createSelector(getReviewsState, (state: ReviewsState) =>
    StateUtils.entityValueSelector<ReviewsInfo>(state, productCode),
  );

export const getReviewsLoading = (productCode: string): MemoizedSelector<StateWithReviews, boolean> =>
  createSelector(getReviewsState, (state: ReviewsState) =>
    StateUtils.entityLoadingSelector<ReviewsInfo>(state, productCode),
  );

export const getReviewsError = (productCode: string): MemoizedSelector<StateWithReviews, boolean> =>
  createSelector(getReviewsState, (state: ReviewsState) =>
    StateUtils.entityErrorSelector<ReviewsInfo>(state, productCode),
  );

export const getReviewsSuccess = (productCode: string): MemoizedSelector<StateWithReviews, boolean> =>
  createSelector(getReviewsState, (state: ReviewsState) =>
    StateUtils.entitySuccessSelector<ReviewsInfo>(state, productCode),
  );

export const getAddReviewState: MemoizedSelector<StateWithReviews, AddReviewState> = createSelector(
  getCustomReviewsState,
  (state: ReviewsFeatureState) => state.addReview,
);

export const getAddReviewLoading: MemoizedSelector<StateWithReviews, boolean> = createSelector(
  getAddReviewState,
  (state: AddReviewState) => StateUtils.loaderLoadingSelector(state),
);

export const getAddReviewError: MemoizedSelector<StateWithReviews, boolean> = createSelector(
  getAddReviewState,
  (state: AddReviewState) => StateUtils.loaderErrorSelector(state),
);

export const getAddReviewSuccess: MemoizedSelector<StateWithReviews, boolean> = createSelector(
  getAddReviewState,
  (state: AddReviewState) => StateUtils.loaderSuccessSelector(state),
);

export const getProductListReviewState: MemoizedSelector<StateWithReviews, ProductListReviewState> = createSelector(
  getCustomReviewsState,
  (state: ReviewsFeatureState) => state.productListReview,
);

export const getProductListReviewValue: MemoizedSelector<StateWithReviews, ProductListReviewsCollection> =
  createSelector(getProductListReviewState, (state: ProductListReviewState) => StateUtils.loaderValueSelector(state));

export const getProductListReviewLoading: MemoizedSelector<StateWithReviews, boolean> = createSelector(
  getProductListReviewState,
  (state: ProductListReviewState) => StateUtils.loaderLoadingSelector(state),
);

export const getProductListReviewError: MemoizedSelector<StateWithReviews, boolean> = createSelector(
  getProductListReviewState,
  (state: ProductListReviewState) => StateUtils.loaderErrorSelector(state),
);

export const getProductListReviewSuccess: MemoizedSelector<StateWithReviews, boolean> = createSelector(
  getProductListReviewState,
  (state: ProductListReviewState) => StateUtils.loaderSuccessSelector(state),
);
